/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Modal, Row } from "react-bootstrap";
import { Controller, useForm, useFormContext } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, FieldGroup, Form, Input } from "core/components";
import { toTitleCase, toBase64 } from "core/helpers";
import { DocumentFormData, DocumentSchema, TemplateFormData } from "core/schema";

type DocumentFormProps = {
  title: string;
  document: DocumentFormData;
  show: boolean;
  readOnly: boolean;
};

const DocumentForm = ({ title, document, show, readOnly, onClose, onConfirm }: DocumentFormProps): JSX.Element => {
  const { document_type } = document;
  const {
    control,
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<DocumentFormData>({
    mode: "onChange",
    defaultValues: DocumentSchema.cast(document),
    resolver: yupResolver(DocumentSchema)
  });

  React.useEffect(() => {
    return () => {};
  }, []);

  return (
    <React.StrictMode>
      <Modal show={show}>
        <Modal.Header>{title && <Modal.Title>{title}</Modal.Title>}</Modal.Header>
        <Modal.Body>
          <Form>
            <Container>
              <Row>
                <Col>
                  {readOnly ? (
                    <Input
                      label="File"
                      className="form-control"
                      placeholder="STD-01MIRANDA-1001"
                      errorMessage={errors?.file?.message}
                      value={document?.file_name}
                      readOnly={true}
                    />
                  ) : (
                    <Input
                      {...register(`${fieldItemName}.file`)}
                      label="File"
                      type="file"
                      required
                      className="form-control"
                      placeholder="STD-01MIRANDA-1001"
                      errorMessage={errors?.file?.message}
                      onChange={onChange}
                    />
                  )}
                  <Controller
                    name={`${fieldItemName}.description`}
                    control={control}
                    render={({ field }) => (
                      <FieldGroup label="Description" errorMessage={errors?.description?.message}>
                        <textarea
                          {...field}
                          className="field"
                          placeholder={`${toTitleCase(document_type)} for STD-01MIRANDA-1001`}
                          maxLength={255}
                          value={field.value}
                          readOnly={readOnly}
                        />
                      </FieldGroup>
                    )}
                  />
                  <Controller
                    name={`${fieldItemName}.remarks`}
                    control={control}
                    render={({ field }) => (
                      <FieldGroup label="Remarks" errorMessage={errors?.remarks?.message}>
                        <textarea
                          {...field}
                          className="field"
                          placeholder={`${toTitleCase(document_type)} remarks`}
                          maxLength={255}
                          value={field.value}
                          readOnly={readOnly}
                        />
                      </FieldGroup>
                    )}
                  />
                </Col>
              </Row>
            </Container>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={onClose} icon="times" title="Close" />
          {!readOnly && <Button className="button--brand" icon="save" title="Save" onClick={handleSubmit(onConfirm)} />}
        </Modal.Footer>
      </Modal>
    </React.StrictMode>
  );
};

export default DocumentForm;
